import { isNotEmpty } from "neetocist";
import withT from "neetocommons/react-utils/withT";
import { Refresh } from "neetoicons";
import { Button, Typography, Pane, Spinner } from "neetoui";
import PropTypes from "prop-types";

import { REFRESH_BUTTON_TOOLTIP_PROPS } from "./constants";
import Version from "./Version";

const VersionHistory = withT(
  ({
    t,
    isOpen,
    onClose,
    isLoading,
    draftVersions = [],
    publishedVersions = [],
    paneTitle,
    showRefreshButton = false,
    onRefreshClick,
  }) => (
    <Pane {...{ isOpen, onClose }}>
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <Pane.Header className="flex justify-between">
            <Typography
              data-testid="version-history-pane-header"
              lineHeight="normal"
              style="h2"
              weight="semibold"
            >
              {paneTitle || t("neetoMolecules.versionHistory.paneTitle")}
            </Typography>
            {showRefreshButton && (
              <Button
                data-testid="version-history-refresh-button"
                icon={Refresh}
                iconSize={18}
                size="small"
                style="text"
                tooltipProps={REFRESH_BUTTON_TOOLTIP_PROPS}
                onClick={onRefreshClick}
              />
            )}
          </Pane.Header>
          <Pane.Body>
            <div className="flex w-full flex-col gap-y-4">
              {isNotEmpty(draftVersions) && (
                <div className="flex flex-col">
                  {draftVersions.map((version, index) => {
                    const isLast = index === draftVersions.length - 1;

                    return (
                      <Version {...{ ...version, isLast }} key={version.id} />
                    );
                  })}
                </div>
              )}
              {isNotEmpty(publishedVersions) && (
                <div className="flex items-center">
                  <hr className="neeto-ui-border-gray-200 flex-grow" />
                  <Typography
                    className="neeto-ui-bg-gray-200 neeto-ui-text-gray-800 neeto-ui-rounded inline-block px-3 py-1"
                    data-testid="version-history-published-title"
                    lineHeight="normal"
                    style="body3"
                    weight="medium"
                  >
                    {t("neetoMolecules.versionHistory.publishedVersions")}
                  </Typography>
                  <hr className="neeto-ui-border-gray-200 flex-grow" />
                </div>
              )}
              <div className="flex flex-col">
                {publishedVersions.map((version, index) => {
                  const isLast = index === publishedVersions.length - 1;

                  return (
                    <Version {...{ ...version, isLast }} key={version.id} />
                  );
                })}
              </div>
            </div>
          </Pane.Body>
        </>
      )}
    </Pane>
  )
);

VersionHistory.propTypes = {
  /**
   * To specify whether the version history pane should be opened or closed.
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Handler function that is triggered when the close button is clicked.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Boolean flag to show loading state with spinner.
   */
  isLoading: PropTypes.bool,
  /**
   * Array of draft version objects to be displayed in the version history.
   */
  draftVersions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      date: PropTypes.instanceOf(Date).isRequired,
      user: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      isDraft: PropTypes.bool,
      isPublished: PropTypes.bool,
      showMoreDropdown: PropTypes.bool,
      moreDropdownProps: PropTypes.object,
    })
  ),
  /**
   * Array of published version objects to be displayed in the version history.
   */
  publishedVersions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      date: PropTypes.instanceOf(Date).isRequired,
      user: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      isDraft: PropTypes.bool,
      isPublished: PropTypes.bool,
      showMoreDropdown: PropTypes.bool,
      moreDropdownProps: PropTypes.object,
    })
  ),
  /**
   * Custom title to be displayed in the pane header. If not provided, a default title will be used.
   */
  paneTitle: PropTypes.string,
  /**
   * Boolean flag to show or hide the refresh button in the pane header.
   */
  showRefreshButton: PropTypes.bool,
  /**
   * Handler function that is triggered when the refresh button is clicked.
   */
  onRefreshClick: PropTypes.func,
};

export default VersionHistory;
